/*
 * Copyright (c) by INFODATA-GROUP. All rights reserved.
 * No part of this publication may be reproduced, distributed,
 * or transmitted in any form or by any means, including photocopying, recording,
 * or other electronic or mechanical methods, without the prior written permission of the publisher,
 * except in the case of brief quotations embodied in critical
 * reviews and certain other noncommercial uses permitted by copyright law.
 *  author : vhe
 *  date : 02/03/2021 07:39
 */

import {
  Component,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  OnChanges,
  OnDestroy,
  output,
  QueryList,
  signal,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { NgClass, NgStyle, Time } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import {
  CalendarClickEvent,
  CalendarDaySection,
  CalendarDaySubSection,
  CalendarDropTask,
  CalendarTask,
  CommonUtils,
  CustomDatePipe,
  HourSelectionEvent,
  WeekSelectionEvent,
} from '@infodata-group/inf-commons';
import { CalendarView } from '../calendar-view';
import jsPDF from 'jspdf';
import { PdfHelper } from '../export/pdf-helper';
import { DayRowDirective } from './day-row.directive';
import { Telework } from '../telework';
import { TeleworkService } from '../telework.service';
import { DayNotWorkService } from '../day-not-work.service';

@Component({
  selector: 'infl-calendar-view-day',
  templateUrl: './calendar-view-day.component.html',
  styleUrls: ['./calendar-view-day.component.scss'],
  standalone: true,
  imports: [NgClass, NgStyle, DayRowDirective, CustomDatePipe],
})
export class CalendarViewDayComponent
  implements OnChanges, OnDestroy, CalendarView
{
  public start = input({ hours: 7, minutes: 0 });
  public end = input({ hours: 17, minutes: 30 });
  public hourDivCount = input(2);
  public day = input(new Date());
  public tasks = input<CalendarTask[]>();
  public sectionHeight = input(30);
  public canDrag = input.required<boolean>();
  public canResize = input.required<boolean>();
  public canSelect = input.required<boolean>();
  public reduce = input(false);
  public dragByOutside = input<CalendarDropTask<any>>();
  public onDrag = input(false);
  public teleworks = input<Telework[]>(null);
  public resource = input<string>(null);

  public hourSelection = output<WeekSelectionEvent>();
  public hourPreselection = output<WeekSelectionEvent>();
  public taskClick = output<CalendarClickEvent>();
  public taskUpdate = output<CalendarTask>();
  public dragTask = output<{
    task: CalendarTask;
    shift: number;
  }>();
  public dropOutside = output<Time>();
  public taskContextMenu = output<{
    task: CalendarTask;
    style: any;
  }>();
  public taskCreation = output<Date>();
  public taskHover = output<CalendarTask>();
  public clickOnSelection = output<WeekSelectionEvent>();
  public dblClickOnSelection = output<WeekSelectionEvent>();
  public taskDblClick = output<CalendarClickEvent>();

  @ViewChild('taskColumn') taskColumn: ElementRef;
  @ViewChildren('hourSection') hourSections: QueryList<ElementRef>;

  public zones: CalendarDaySection[] = [];
  /**
   * Drag and drop properties
   */
  public columns: { task: CalendarTask; style: any }[][] = [];
  public viewTasks: {
    task: CalendarTask;
    style: any;
    position: number;
    concurrence: number;
    offsetTop?: number;
  }[] = [];
  public selection: { start: Time; end: Time } = { start: null, end: null };
  public selectedZones: CalendarDaySubSection[] = [];
  public isCurrentDay: boolean;
  /**
   * Context menu
   */
  top: number;
  left: number;
  public hourLineStyle: any;
  private sectionCount = 0;
  private draggingTask: { task: CalendarTask; style: any };
  private dragShift: number;
  /**
   * Resize task properties
   */
  private resizeStart: number;
  private resizeTask: {
    border: 'top' | 'bottom';
    task: { task: CalendarTask; style: any; offsetTop?: number };
    element: any;
  };
  private previousTaskWidth: string;
  private baseHeight: number;
  private baseTop: number;
  /**
   * Multi zone selection properties
   */
  private onSelectZone: boolean;
  private saveSelection: { start: Time; end: Time };
  private stopEvent: boolean;
  private interval: any;

  private lastHighlightedElement: HTMLDivElement;

  public dayBackgroundColor = signal('#FFF');

  public readonly translate = inject(TranslateService);
  public readonly teleworkService = inject(TeleworkService);
  public readonly dayNotWorkService = inject(DayNotWorkService);

  @HostListener('document:mouseup', ['$event']) stop(event) {
    this.stopResize();
    this.stopSelection(event);
  }

  constructor() {
    this.checkDayWork();
    effect(() => {
      if (this.teleworks()) {
        this.checkTelework();
      }
    });
  }

  private checkDayWork() {
    const isDayWork = this.dayNotWorkService.isDayWork(this.day());
    if (!isDayWork) {
      this.dayBackgroundColor.set(this.dayNotWorkService.dayNotWorkColor);
    } else {
      this.dayBackgroundColor.set('#FFF');
    }
    return isDayWork;
  }

  private checkTelework() {
    const telework = this.teleworks().find((telework) => {
      return (
        telework.date === CommonUtils.convertDateToString(this.day()) &&
        telework.resourceId === this.resource()
      );
    });
    const isDayWork = this.checkDayWork();
    if (isDayWork && telework) {
      this.dayBackgroundColor.set(this.teleworkService.teleworkColor);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      Object.hasOwn(changes, 'tasks') ||
      (Object.hasOwn(changes, 'day') && this.tasks())
    ) {
      this.generateTasks();
      this.clearHighlightedElement();
      this.draggingTask = null;
    }
    if (Object.hasOwn(changes, 'day')) {
      this.isCurrentDay = CommonUtils.compareDate(this.day(), new Date());
      if (this.isCurrentDay) {
        this.createHourLineStyle();
      }
    }
    if (
      Object.hasOwn(changes, 'onDrag') &&
      !!changes['onDrag'].previousValue !== !!changes['onDrag'].currentValue
    ) {
      if (this.onDrag()) {
        this.disablePointerEvent(this.draggingTask);
      } else {
        this.enablePointerEvent();
      }
    }
    if (
      Object.hasOwn(changes, 'hourDivCount') &&
      this.hourDivCount() &&
      this.tasks()
    ) {
      this.buildZone();
      this.generateTasks();
      this.draggingTask = null;
    }
  }

  public generateTasks() {
    this.viewTasks = [];
    this.columns = [];
    if (this.tasks()) {
      for (const task of this.tasks().sort(
        (t1, t2) =>
          t1.dateStart.toDate().getTime() - t2.dateStart.toDate().getTime(),
      )) {
        if (task.inDay(this.day())) {
          this.generateTask(task);
        }
      }
      for (const vt1 of this.viewTasks) {
        const column: number[] = [];
        for (const vt2 of this.viewTasks) {
          if (
            vt1 !== vt2 &&
            this.checkConcurrence(vt1, vt2) &&
            column.indexOf(vt2.position) < 0
          ) {
            column.push(vt2.position);
          }
        }
        vt1.concurrence = column.length + 1;
        const width = `${100 / vt1.concurrence}%`;
        vt1.style.width = `calc(${width} - 10px)`;
        vt1.style.left = `calc(${width} * ${vt1.position})`;
      }
    }
  }

  public getTopOffset(task: CalendarTask): number {
    if (CommonUtils.compareDate(task.dateStart.toDate(), this.day())) {
      const minutes =
        (task.dateStart.get('h') - this.start().hours) * 60 +
        (task.dateStart.get('m') - this.start().minutes);
      const minutesOfSection = 60 / this.hourDivCount();
      return (minutes / minutesOfSection) * this.sectionHeight();
    } else {
      return 0;
    }
  }

  onDragStart(task: { task: CalendarTask; style: any }, event: DragEvent) {
    event.dataTransfer.setDragImage(event.target as any, 0, 0);
    if (this.canDrag()) {
      this.draggingTask = task;
      this.dragShift =
        event.clientY - (<any>event.target).getBoundingClientRect().top;
      this.dragTask.emit({ task: task.task, shift: this.dragShift });
      this.disablePointerEvent(task);
    }
  }

  onDropTask(cell: CalendarDaySection, sub: CalendarDaySubSection) {
    if (this.canDrag()) {
      if (this.draggingTask) {
        let m = this.draggingTask.task.dateStart.clone();
        // const shift = Math.floor(this.dragShift / this.sectionHeight);
        // const shiftMinutes = (60 / this.hourDivCount) * shift;
        // const minutes = cell.hour * 60 + sub.minutes - shiftMinutes;
        const minutes = cell.hour * 60 + sub.minutes;
        if (minutes < 0) {
          m = m
            .hour(Math.floor(this.start().hours))
            .minute(this.start().minutes);
        } else {
          m = m.hour(Math.floor(minutes / 60)).minute(minutes % 60);
        }
        this.draggingTask.task.updateDateStart(m);
        this.taskUpdate.emit(this.draggingTask.task);
        this.draggingTask = null;
        this.generateTasks();
      } else if (this.dragByOutside()) {
        const dateStart = new Date(this.day());
        dateStart.setHours(cell.hour, sub.minutes);
        this.taskCreation.emit(dateStart);
      } else {
        this.dropOutside.emit({ hours: cell.hour, minutes: sub.minutes });
      }
    }
  }

  allowDrop(event: DragEvent) {
    event.preventDefault();
    const elements = document.elementsFromPoint(event.x, event.y);
    for (const elem of elements) {
      if (elem.className === 'sub') {
        this.clearHighlightedElement();
        (elem as HTMLDivElement).style.backgroundColor = '#FFF2DA';
        this.lastHighlightedElement = elem as HTMLDivElement;
      }
    }
  }

  private clearHighlightedElement() {
    if (this.lastHighlightedElement) {
      this.lastHighlightedElement.style.backgroundColor = null;
      this.lastHighlightedElement = null;
    }
  }

  onDrop(event: DragEvent) {
    const elements = document.elementsFromPoint(event.x, event.y);
    for (const elem of elements) {
      if (elem.className === 'sub') {
        const hour = parseInt(elem['dataset'].hour, 10);
        const minutes = parseInt(elem['dataset'].time, 10);
        const section = this.zones.find((zone) => zone.hour === hour);
        const sub = section.zones.find((s) => s.minutes === minutes);
        this.onDropTask(section, sub);
      }
    }
  }

  onTaskResize(
    task: { task: CalendarTask; style: any },
    event: MouseEvent,
    border: 'top' | 'bottom',
  ) {
    if (this.canResize()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.resizeStart = event.y;
      const target = (<any>event.target).offsetParent;
      this.previousTaskWidth = target.style.width;
      target.style.width = '100%';
      target.style.opacity = '0.3';
      this.baseHeight = target.clientHeight;
      this.baseTop = target.offsetTop;
      this.resizeTask = { element: target, task: task, border: border };
    }
  }

  onMouseMove(event: MouseEvent) {
    if (this.resizeTask) {
      if (this.resizeTask.border === 'top') {
        this.resizeTask.element.style.top = `${
          this.baseTop + (event.y - this.resizeStart)
        }px`;
        this.resizeTask.element.style.height = `${
          this.baseHeight - (event.y - this.resizeStart)
        }px`;
      } else {
        this.resizeTask.element.style.height = `${
          this.baseHeight + (event.y - this.resizeStart)
        }px`;
      }
    }
  }

  selectZone(event: MouseEvent, hour: number, sub: CalendarDaySubSection) {
    if (this.canSelect()) {
      if (!sub.selected) {
        event.stopPropagation();
        event.preventDefault();
        for (const section of this.selectedZones) {
          section.selected = false;
        }
        this.selectedZones = [sub];
        sub.select(true);
        this.onSelectZone = true;
        this.selection.start = { hours: hour, minutes: sub.minutes };
      } else if (event.which === 3) {
        this.stopEvent = true;
        const hourSelection = new HourSelectionEvent(this.saveSelection, event);
        this.clickOnSelection.emit(
          new WeekSelectionEvent(this.day(), hourSelection),
        );
      }
    }
  }

  overZone(
    event: MouseEvent,
    hour: number,
    minutes: number,
    sub: CalendarDaySubSection,
  ) {
    if (this.selection.start) {
      for (const section of this.selectedZones) {
        section.selected = false;
      }
      this.selection.end = { hours: hour, minutes: minutes };
      for (const zone of this.zones) {
        const sections = zone.select(this.selection.start, this.selection.end);
        if (sections) {
          for (const s of sections) {
            this.selectedZones.push(s);
          }
        }
      }
    } else {
      const selection = { start: null, end: null };
      selection.start = { hours: hour, minutes: minutes };
      selection.end = { hours: hour, minutes: minutes + sub.minutes };
      const preselection = new HourSelectionEvent(selection, event);
      this.hourPreselection.emit(
        new WeekSelectionEvent(this.day(), preselection),
      );
    }
  }

  onTaskClick(event: MouseEvent, task: CalendarTask) {
    if (this.resizeTask) {
      return;
    }
    this.taskClick.emit(new CalendarClickEvent(event, task));
  }

  deselect(): void {
    if (!this.selectedZones) {
      return;
    }
    for (const section of this.selectedZones) {
      section.selected = false;
    }
  }

  onRightClick(event: MouseEvent) {
    if (this.stopEvent) {
      event.stopPropagation();
      event.preventDefault();
      this.stopEvent = false;
    }
  }

  onDblClick(event: MouseEvent) {
    const hourSelection = new HourSelectionEvent(this.saveSelection, event);
    this.dblClickOnSelection.emit(
      new WeekSelectionEvent(this.day(), hourSelection),
    );
  }

  onTaskDblClick(event: MouseEvent, task: CalendarTask) {
    event.stopPropagation();
    event.preventDefault();
    this.taskDblClick.emit(new CalendarClickEvent(event, task));
  }

  ngOnDestroy(): void {
    if (this.interval) {
      clearInterval(this.interval);
    }
  }

  /**
   * enable pointerEvent to catch drop event on zone, not on task
   * @private
   */
  public enablePointerEvent() {
    this.viewTasks.forEach((t) => {
      t.style['pointer-events'] = 'auto';
    });
  }

  public findTask(id: string): { style: any; task: CalendarTask } {
    return this.viewTasks.find((vt) => vt.task.id === id);
  }

  getScrollPosition(): { x: number; y: number } {
    return null;
  }

  setScrollPosition(x: number, y: number) {
    // TODO Nothing
  }

  private buildZone() {
    this.zones = [];
    this.sectionCount = 0;
    let i = this.start().hours;
    for (i; i < this.end().hours; ++i) {
      const section = new CalendarDaySection(i);
      section.generateSubZones(this.start(), this.end(), this.hourDivCount());
      this.sectionCount += section.length;
      this.zones.push(section);
    }
    if (this.end().minutes > 0) {
      const section = new CalendarDaySection(i);
      section.generateSubZones(this.start(), this.end(), this.hourDivCount());
      this.sectionCount += section.length;
      this.zones.push(section);
    }
  }

  private generateTask(task: CalendarTask) {
    let top = Math.round(this.getTopOffset(task) + 9);
    let offsetTop = 0;
    const h = this.getHeight(task);
    let height: string;
    if (h === 'full') {
      height = `calc(100% - ${top}px)`;
    } else {
      height = Math.round(h + 1) + 'px';
      if (top <= 0) {
        height = Math.round(h - 8) + 'px';
        offsetTop = top - 9;
        top = 9;
      }
    }
    const style = {
      'top.px': top,
      height: height,
      // 'width.px': width.width - 4,
      color: task.color,
      borderColor: task.borderColor ?? task.backgroundColor,
    };
    const viewTask = {
      task: task,
      style: style,
      offsetTop,
      position: 0,
      concurrence: 1,
    };
    const column = this.getColumn(viewTask);
    if (this.columns[column]) {
      this.columns[column].push(viewTask);
    } else {
      this.columns[column] = [viewTask];
    }
    viewTask.position = column;
    this.viewTasks.push(viewTask);
  }

  private getHeight(task: CalendarTask): number | 'full' {
    const minutes = task.getMinutesForDay(this.day(), this.start(), this.end());
    const minutesOfSection = 60 / this.hourDivCount();
    if (minutes === 'full') {
      return 'full';
    } else {
      return (minutes / minutesOfSection) * this.sectionHeight();
    }
  }

  private getColumn(task: { task: CalendarTask; style: any }): number {
    let index = 0;
    for (const column of this.columns) {
      let free = true;
      for (const vt of column) {
        if (this.checkConcurrence(task, vt)) {
          free = false;
          break;
        }
      }
      if (free) {
        return index;
      } else {
        ++index;
      }
    }
    return this.columns.length;
  }

  private stopResize() {
    if (this.resizeTask) {
      this.resizeTask.element.style.opacity = '1';
      this.resizeTask.element.style.width = this.previousTaskWidth;
      const offsetTop = this.resizeTask.task.offsetTop ?? 0;
      const height = this.resizeTask.element.clientHeight - offsetTop;
      const precision = 60 / this.hourDivCount();
      const pixelByMinutes = this.sectionHeight() / precision;
      const duration =
        Math.round(height / pixelByMinutes / precision) * precision;
      const task = this.resizeTask.task.task;
      if (this.resizeTask.border === 'top') {
        task.dateStart = task.dateEnd.clone().subtract(duration, 'm');
        task.around('start', 60 / this.hourDivCount());
      } else {
        task.dateEnd = task.dateStart.clone().add(duration, 'm');
        task.around('end', 60 / this.hourDivCount());
      }
      this.taskUpdate.emit(task);
      setTimeout(() => {
        this.resizeTask = null;
      }, 100);
      this.generateTasks();
    }
  }

  private checkTime() {
    const start = CommonUtils.convertStrToTimeInMillis(
      CommonUtils.convertTimeToString(this.selection.start),
    );
    const end = CommonUtils.convertStrToTimeInMillis(
      CommonUtils.convertTimeToString(this.selection.end),
    );
    if (start >= end) {
      const temp = this.selection.end;
      this.selection.end = this.selection.start;
      this.selection.start = temp;
    }
  }

  private stopSelection(event: MouseEvent) {
    if (this.selection.start) {
      if (!this.selection.end) {
        this.selection.end = this.selection.start;
      }
      this.checkTime();
      this.setSelectionEnd(
        this.selection.end.hours,
        this.selection.end.minutes,
      );
      const hourSelection = new HourSelectionEvent(this.selection, event);
      this.hourSelection.emit(
        new WeekSelectionEvent(this.day(), hourSelection),
      );
      this.saveSelection = this.selection;
    }
    this.selection = { start: null, end: null };
  }

  private setSelectionEnd(hour: number, minutes: number) {
    let m = hour * 60 + minutes + 60 / this.hourDivCount();
    const h = Math.floor(m / 60);
    m = m % 60;
    this.selection.end = { hours: h, minutes: m };
  }

  private createHourLineStyle() {
    this.computeHourLineHeight();
    this.interval = setInterval(() => {
      this.computeHourLineHeight();
    }, 60000);
  }

  private checkConcurrence(
    task: { task: CalendarTask; style: any },
    vt: { task: CalendarTask; style: any },
  ): boolean {
    const tStart = task.task.dateStart.toDate().getTime();
    const tEnd = task.task.dateEnd.toDate().getTime();
    const vtStart = vt.task.dateStart.toDate().getTime();
    const vtEnd = vt.task.dateEnd.toDate().getTime();
    const firstCondition =
      (tStart >= vtStart && tStart < vtEnd) ||
      (tStart <= vtStart && tEnd > vtStart);
    const secondCondition =
      (tEnd > vtStart && tEnd <= vtEnd) ||
      (tEnd < vtStart && tStart >= vtStart);
    return firstCondition || secondCondition;
  }

  private computeHourLineHeight() {
    const d = new Date();
    const currentTime = d.getHours() * 60 + d.getMinutes();
    const start = this.start().hours * 60 + this.start().minutes;
    const minutesOfSection = 60 / this.hourDivCount();
    const m =
      ((currentTime - start) / minutesOfSection) * this.sectionHeight() + 40;
    this.hourLineStyle = {
      top: m + 'px',
    };
  }

  /**
   * disable pointerEvent to catch drop event on zone, not on task
   * @param task
   * @private
   */
  private disablePointerEvent(task?: { task: CalendarTask; style: any }) {
    this.viewTasks.forEach((t) => {
      if (!task || t.task.id !== task.task.id) {
        t.style['pointer-events'] = 'none';
      } else if (task) {
        // todo to improve when we have time
        setTimeout(() => {
          task.style['pointer-events'] = 'none';
        }, 1);
      }
    });
  }

  scrollToDate(date: Date): void {
    if (CommonUtils.compareDate(date, this.day())) {
      const h = date.getHours();
      const section = this.hourSections.find(
        (s) => parseFloat(s.nativeElement.dataset.hour) === h,
      );
      if (section) {
        section.nativeElement.scrollIntoView({ block: 'center' });
      }
    }
  }

  toPdf(
    doc: jsPDF,
    width: number,
    x: number,
    height?: number,
    last = false,
  ): void {
    let y = 30;
    height = doc.internal.pageSize.height;
    // Vertical row
    if (!last) {
      doc.line(x + width, 70, x + width, height - 20);
    }
    doc.setFontSize(13);
    // Horizontal row
    doc.text(CommonUtils.convertDateToString(this.day()), x + 5, y + 60);
    doc.setFontSize(11);
    const columnWidth = (width - 10) / this.columns.length;
    y = 100;
    x -= 10;
    for (const c of this.columns) {
      for (const t of c) {
        const taskTop = t.style['top.px'] - 9;
        const taskHeight = parseFloat(t.style.height.slice(0, -2)) - 1;
        PdfHelper.setFillColor(doc, t.style.backgroundColor);
        doc.rect(x + 11, taskTop + y, columnWidth - 21, taskHeight, 'F');
        const text = PdfHelper.generateTextLine(
          doc,
          t.task.label,
          columnWidth - 21,
          taskHeight,
        );
        doc.text(text, x + 13, taskTop + y + 20);
      }
      x += columnWidth;
    }
  }

  onDragLeave() {
    this.clearHighlightedElement();
  }
}
